import React from "react";
import ReactDOM from "react-dom";
import { Modal as AntModal } from "antd";
import type { ModalProps as AntModalProps } from "antd";
import { createGlobalStyle } from "styled-components";
import { Cancel01Icon } from "@hugeicons/core-free-icons";
import HugeIcon from "src/components/HugeIcon";
import { colors } from "src/theme/colors";
import { line, radii, scrim, shadow } from "src/theme/ui";

/**
 * Modal — football's centred dialog, ported.
 *
 * An 18px corner on a hairline over a plain ink scrim — no blur, because that
 * design dims the page and never frosts it — under the modal shadow, a shadow
 * here meaning "this can be closed". Its chrome: a 15px/600 title in a
 * hairline-ruled head with a 32px muted close square, and a sunk foot band
 * carrying the actions.
 *
 * The original is Radix Dialog; Ant Design's Modal is kept underneath because
 * it owns the same behaviour worth having — focus moves in on open and returns
 * to the trigger on close, focus is trapped while open, Escape dismisses.
 */

/* `xl` is for a dialog whose body is a TABLE — a list of securities with
   eight columns cannot be read at 672 and has nowhere narrower to go. */
const SIZES = { sm: 384, md: 512, lg: 672, xl: 900 } as const;

const ModalStyles = createGlobalStyle`
  .fina-modal-wrap .ant-modal {
    padding-bottom: 0;
  }

  /**
   * The dialog is bounded by the WINDOW, and its body is what gives way.
   *
   * Nothing capped it before, so a dialog whose content was taller than the
   * screen grew past it: the head scrolled off the top of the viewport, the
   * actions sat below the bottom of it, and reaching either meant scrolling
   * the whole dialog inside its own scrim. Measured on the invest form, the
   * body came to 1127px — no laptop has that.
   *
   * A column instead: the head and the foot band hold their size, the body
   * takes what is left and scrolls. So the title stays visible while you
   * work and the actions stay where the reader learned they were, which is
   * the point of having a foot band at all.
   */
  .fina-modal-wrap .ant-modal {
    max-width: calc(100vw - 32px);
  }

  .fina-modal-wrap .ant-modal-content {
    display: flex;
    flex-direction: column;
    max-height: calc(100vh - 48px);
    overflow: hidden;
    border: 1px solid ${line.hairline};
    border-radius: ${radii.modal}px;
    background: ${colors.white};
    box-shadow: ${shadow.pop};
  }

  .fina-modal-wrap .ant-modal-header,
  .fina-modal-wrap .ant-modal-footer {
    flex: none;
  }

  .fina-modal-wrap .ant-modal-header {
    /* The right gutter clears the close button.
       The head was padded 20px on both sides while the X sits 20px from the
       right edge
       and is 32px wide, so a title or a description long enough to reach the
       corner ran underneath it — and every dialog with a sentence in its head
       is long enough. 20 + 32 + 8 of air. */
    padding: 15px 60px 15px 20px;
    border-bottom: 1px solid ${line.hairline};
    border-radius: 0;
    background: ${colors.white};
  }

  .fina-modal-wrap .ant-modal-title {
    font-size: 15px;
    font-weight: 600;
    letter-spacing: -0.014em;
    color: ${colors.gray[900]};
  }

  .fina-modal-wrap .fina-modal-description {
    margin: 3px 0 0;
    font-size: 12px;
    line-height: 1.45;
    color: ${colors.gray[600]};
  }

  .fina-modal-wrap .ant-modal-body {
    /* A zero min-height is what lets it shrink: a flex item defaults to its
       content's height and would otherwise push the column past the cap it
       was just given. */
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    padding: 20px;
    font-size: 13px;
    color: ${colors.gray[800]};
  }

  /* An empty band is a strip of nothing between the body and the dialog's
     edge, and the slot is empty on the first paint and in every dialog that
     does not use it. */
  .fina-modal-wrap .ant-modal-footer:has(> .fina-modal-slot:empty),
  .fina-modal-wrap .ant-modal-footer:empty {
    display: none;
  }

  /* A sunk band, so the actions read as a footer rather than as more body. */
  .fina-modal-wrap .ant-modal-footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    padding: 13px 20px;
    border-top: 1px solid ${line.hairline};
    background: ${colors.gray[25]};
  }

  /* The slot IS the band, not a box sitting in it.
     It carried no width of its own, so as a flex item in the band it shrank
     to its content — measured at 87px. A form's action row lands in here and
     goes stacked-and-full-width below the tablet breakpoint; resolved against
     an 87px slot that came out as two narrow buttons one above the other,
     right-aligned, in a band 127px tall to hold 36px of button. */
  .fina-modal-wrap .fina-modal-slot {
    flex: 1;
    min-width: 0;
  }

  /* What lands in the band is the band's own row. A form's action row carries
     a top margin for its place in a page's flow; inside a footer that margin
     is only height, and the band brings its own padding. */
  .fina-modal-wrap .ant-modal-footer .fina-modal-slot > *,
  .fina-modal-wrap .ant-modal-footer .fina-modal-slot > * > * {
    margin-top: 0;
    margin-bottom: 0;
  }

  /* Ant Design puts the close control in the corner as a bare glyph; the
     design gives it a square of its own, in line with the head's padding. */
  .fina-modal-wrap .ant-modal-close {
    top: 15px;
    right: 20px;
    width: auto;
    height: auto;
  }

  .fina-modal-wrap .ant-modal-close-x {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border: 1px solid ${line.field};
    border-radius: ${radii.control}px;
    background: ${colors.gray[50]};
    color: ${colors.gray[700]};
    font-size: 0;
    line-height: 1;
    transition: background 0.15s ease, color 0.15s ease;
  }

  .fina-modal-wrap .ant-modal-close:hover .ant-modal-close-x {
    background: ${colors.gray[100]};
    color: ${colors.gray[900]};
  }

  .fina-modal-wrap .ant-modal-close:focus-visible .ant-modal-close-x {
    box-shadow: ${shadow.focusRing};
  }
`;

export interface ModalProps
  extends Omit<AntModalProps, "width" | "footer" | "title"> {
  title: React.ReactNode;
  /** A line under the title, for what the dialog needs the reader to know. */
  description?: React.ReactNode;
  footer?: React.ReactNode;
  size?: keyof typeof SIZES;
}

/**
 * The foot of the dialog, for content that is declared somewhere else.
 *
 * Three dialogs in this product host a whole page flow — depositing,
 * withdrawing, investing — and a flow's actions depend on the flow's own
 * state: which bank is chosen, whether the amount clears its minimum, which
 * step is showing. Passing them to the shell as a footer prop means
 * lifting all of that up a level, which for a money flow is a change with
 * more risk in it than the fault being fixed.
 *
 * So the shell offers a SLOT instead. It renders the footer band and puts
 * the node on a context; anything inside the dialog can render into it with
 * DialogFooter, from wherever in the tree it happens to live, and the
 * actions still end up in the one place that cannot scroll. One mechanism
 * for every dialog — the alternative was a sticky band inside the body,
 * which is a second answer to the same question and breaks the moment an
 * ancestor between it and the scroller has an overflow of its own.
 */
const FooterSlot = React.createContext<HTMLElement | null>(null);

export const DialogFooter: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const slot = React.useContext(FooterSlot);
  /* Nothing to portal into means this is not inside a dialog — a page
     rendering the same flow — so the caller keeps its own layout. */
  return slot ? ReactDOM.createPortal(children, slot) : null;
};

const Modal: React.FC<ModalProps> = ({
  title,
  description,
  footer,
  size = "md",
  children,
  ...props
}) => {
  const [slot, setSlot] = React.useState<HTMLElement | null>(null);

  /* A callback ref rather than a plain one: the children render in the same
     commit as the band, and a ref object would still be null when they look
     for it. State makes the slot's arrival a render the portal can see. */
  const holdSlot = React.useCallback(
    (node: HTMLDivElement | null) => setSlot(node),
    [],
  );

  return (
    <>
      <ModalStyles />
      <AntModal
        width={SIZES[size]}
        centered
        wrapClassName="fina-modal-wrap"
        /* The mask is a sibling of the wrap, not a descendant, so a scoped rule
         cannot reach it — and a global one would restyle every other modal in
         the app. Inline is the narrow way to dim only this dialog: plain ink,
         never frosted. */
        maskStyle={{ background: scrim, backdropFilter: "none" }}
        closeIcon={<HugeIcon icon={Cancel01Icon} size={15} />}
        title={
          <>
            <div>{title}</div>
            {description && (
              <p className="fina-modal-description">{description}</p>
            )}
          </>
        }
        /* The band is rendered whenever a caller has NOT passed its own
         footer, so a flow inside can fill it. An empty band would be a
         visible strip of nothing, so it collapses until something lands in
         it — see the :empty rule in the styles above. */
        footer={footer ?? <div className="fina-modal-slot" ref={holdSlot} />}
        {...props}
      >
        <FooterSlot.Provider value={slot}>{children}</FooterSlot.Provider>
      </AntModal>
    </>
  );
};

export default Modal;
